import { asyncCallRoutePost } from '../helpers/api';

/**
 * Class for handling all custom functionality on view order page (/my-account/view-order/123/)
 */
export class AddCustomerForm {
	constructor( element ) {
		this.element = element;
		this.form = this.element.querySelector( '.js-qala-b2b-add-customer-form' );
		this.emailElement = this.element.querySelector( '.js-qala-b2b-add-customer-email' );
		this.firstNameElement = this.element.querySelector( '.js-qala-b2b-add-customer-first-name' );
		this.lastNameElement = this.element.querySelector( '.js-qala-b2b-add-customer-last-name' );
		this.emailFieldNotice = this.element.querySelector( '.js-qala-b2b-add-customer-email-notice' );
		this.emailFormRowElement = this.element.querySelector( '.js-qala-b2b-add-customer-email-form-row' );
		this.emailFieldWrap = this.element.querySelector( '.js-qala-b2b-add-customer-email-field-wrap' );
		this.saveOrderButtonElement = this.element.querySelector( '.js-qala-b2b-save-order-button' );
		this.initialEmailValue = this.emailElement.value;
		this.orderId = this.element.getAttribute( 'data-order-id' );

		this.isValidationInProgress = false;

		const { routeUrl, _wpnonce } = window.QalaB2bWholesale.validateCustomerEmailRouteObj;

		this.updateOrderRouteUrl = window.QalaB2bWholesale.updateOrderRouteObj.routeUrl;
		this.updateOrderNonce = window.QalaB2bWholesale.updateOrderRouteObj._wpnonce;

		this.routeUrl = routeUrl;
		this._wpnonce = _wpnonce;
	}

	/**
	 * Initialize the view order page with all needed callbacks
	 *
	 * @return {void}
	 */
	init() {
		this.emailElement.addEventListener( 'blur', this.validateCustomerEmail.bind( this ) );
		this.setupSaveOrderButton();
	}

	/**
	 * Setup the save order button.
	 *
	 * @return {void}
	 */
	setupSaveOrderButton() {
		if ( ! this.saveOrderButtonElement ) {
			return;
		}

		this.saveOrderButtonElement.addEventListener( 'click', this.saveOrder.bind( this ) );
	}

	/**
	 * Send the request for saving order.
	 *
	 * @param {Event} event
	 */
	async saveOrder( event ) {
		event.preventDefault();
		let customerData = {};

		// Make sure the email isn't currently being validated
		if ( this.isValidationInProgress ) {
			return;
		}

		customerData = {
			customer_email: this.emailElement.value,
			customer_first_name: this.firstNameElement.value,
			customer_last_name: this.lastNameElement.value,
		};

		this.startValidating();
		try {
			const response = await asyncCallRoutePost( this.updateOrderRouteUrl, this.updateOrderNonce, {
				order_id: this.orderId,
				...customerData,
			} );
			this.processResponse( response );
		} catch ( e ) {
			this.processErrorResponse();
		}

		this.endValidating();
	}

	/**
	 * Start the request for validating customer email.
	 *
	 * @return {void}
	 */
	startValidating() {
		this.isValidationInProgress = true;
		this.saveOrderButtonElement.disabled = true;
		this.emailElement.disabled = true;
		this.clearNotice();
	}

	/**
	 * End the request for validating customer email.
	 *
	 * @return {void}
	 */
	endValidating() {
		this.isValidationInProgress = false;
		this.emailElement.disabled = false;
		this.saveOrderButtonElement.disabled = false;
	}

	/**
	 * Validates customer email, sets the first name last name if needed.
	 *
	 * @param {Event} event
	 */
	async validateCustomerEmail( event ) {
		const email = event.target.value;
		event.preventDefault();

		// Check if the entered value is the same as initially loaded.
		if ( email === this.initialEmailValue && this.initialEmailValue.length > 0 ) {
			this.clearNotice();
			return;
		}

		const isValid = this.form.checkValidity() && this.emailElement.value !== '';
		this.reportValidity( isValid );
		if ( ! isValid ) {
			return;
		}

		this.startValidating();
		try {
			const response = await asyncCallRoutePost( this.routeUrl, this._wpnonce, { email } );
			this.processResponseForValidation( response );
		} catch ( e ) {
			this.processErrorResponse();
		}

		this.endValidating();
	}

	/**
	 * Process the response from the server, assuming everything is ok (no server error)
	 *
	 * @param {Object} response
	 */
	processResponse( response ) {
		this.emailFieldNotice.innerHTML = response.message;
		let isSuccess = false;

		if ( response?.data?.status && response.data.status === 200 ) {
			isSuccess = true;
		}

		if ( isSuccess ) {
			this.setSuccessNotice( response.message );
		} else {
			this.setErrorNotice( response.message );
		}

		this.prefillNameFields( response.data );
	}

	/**
	 * Process the response for email validation.
	 *
	 * @param {Object} response Response from the server.
	 * @return {void}
	 */
	processResponseForValidation( response ) {
		this.emailFieldNotice.innerHTML = response.message;
		let isSuccess = false;

		if ( response?.data?.status && response.data.status === 200 ) {
			isSuccess = true;
		}

		if ( isSuccess ) {
			this.setWarningNotice( response.message );
		} else {
			this.setErrorNotice( response.message );
		}

		this.prefillNameFields( response.data );
	}

	/**
	 * Process the error response from the server, happens if there is a server error (500)
	 *
	 * @return {void}
	 */
	processErrorResponse() {
		this.setErrorNotice( window.QalaB2bWholesale.text.unknownError );
	}

	/**
	 * Set the success notice.
	 *
	 * @param {string} text Text to set.
	 * @return {void}
	 */
	setSuccessNotice( text ) {
		this.emailFieldNotice.innerHTML = text;
		this.emailFieldNotice.classList.remove( 'woocommerce-error' );
		this.emailFieldNotice.classList.remove( 'woocommerce-info' );
		this.emailFieldNotice.classList.add( 'woocommerce-message' );
	}

	/**
	 * Set the warning notice.
	 *
	 * @param {string} text Text to set.
	 * @return {void}
	 */
	setWarningNotice( text ) {
		this.emailFieldNotice.innerHTML = text;
		this.emailFieldNotice.classList.remove( 'woocommerce-message' );
		this.emailFieldNotice.classList.remove( 'woocommerce-error' );
		this.emailFieldNotice.classList.add( 'woocommerce-info' );
	}

	/**
	 * Set the error notice.
	 *
	 * @param {string} text Text to set.
	 * @return {void}
	 */
	setErrorNotice( text ) {
		this.emailFieldNotice.innerHTML = text;
		this.emailFieldNotice.classList.remove( 'woocommerce-info' );
		this.emailFieldNotice.classList.remove( 'woocommerce-message' );
		this.emailFieldNotice.classList.add( 'woocommerce-error' );
	}

	/**
	 * Clear the notice.
	 *
	 * @return {void}
	 */
	clearNotice() {
		this.emailFieldNotice.innerHTML = '';
		this.emailFieldNotice.classList.remove( 'woocommerce-message' );
		this.emailFieldNotice.classList.remove( 'woocommerce-info' );
		this.emailFieldNotice.classList.remove( 'woocommerce-error' );
	}

	/**
	 * Prefill the name fields if needed.
	 *
	 * @param {Object} data Data from the server response.
	 * @return {void}
	 */
	prefillNameFields( data ) {
		this.firstNameElement.disabled = false;
		this.lastNameElement.disabled = false;

		if ( ! data?.isExistingUser ) {
			return;
		}

		this.firstNameElement.value = data.firstName;
		this.lastNameElement.value = data.lastName;
		this.firstNameElement.disabled = true;
		this.lastNameElement.disabled = true;
	}

	/**
	 * Report validity of the email field.
	 *
	 * @param {boolean} isValid Is the email valid.
	 * @return {void}
	 */
	reportValidity( isValid ) {
		if ( isValid ) {
			this.emailFormRowElement.classList.remove( 'woocommerce-invalid' );
			this.clearNotice();
		} else {
			this.emailFormRowElement.classList.add( 'woocommerce-invalid' );
			this.setErrorNotice( window.QalaB2bWholesale.text.emailInvalid );
		}
	}
}
